// Carga del camión de AGENCIAS — escaneo continuo de TODOS los paquetes.
//
// Flujo (ver charla con el usuario):
//  1. El encargado abre esta pantalla y escanea, seguido, cada paquete/bulto de
//     cada pedido. La app detecta solo a qué pedido pertenece cada QR (bultos y
//     paquetes) usando las claves de unidadesDeItem.
//  2. Se acumula TODO en memoria + respaldo en localStorage (si la tablet se
//     recarga, se recupera lo escaneado). NO se escribe a Firestore por paquete.
//  3. Al tocar "Finalizar carga de AGENCIAS": si hay pedidos a medias, avisa; los
//     COMPLETOS se marcan CARGADO en un solo batch y se guarda el historial
//     (cargas_agencia) con hora de inicio/fin y los paquetes por pedido.
//
// Solo se pueden cargar pedidos despachados+facturados: los que llegan en `pedidos`
// ya son los despachados; si un QR no está en la lista, es porque no está
// despachado/facturado (o no es de agencia) → mensaje genérico.

import React, { useRef, useState, useCallback, useMemo, useEffect } from "react";
import { doc, serverTimestamp, writeBatch, addDoc, collection, Timestamp } from "firebase/firestore";
import { db } from "../../../firebase";
import QrScanner from "../../../components/QrScanner";
import { useAuth, hasRol } from "../../../context/AuthContext";
import { unidadesDeItem } from "../services/etiquetasFS";
import { AGENCIAS } from "../services/parseDescripcionPedido";

// ── Sonidos (Web Audio, sin archivos) ──────────────────────────
let _audioCtx = null;
function ctx() {
  try {
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    return _audioCtx;
  } catch { return null; }
}
function tono(freqs, dur = 0.12, vol = 0.2, type = "sine") {
  const c = ctx(); if (!c) return;
  const o = c.createOscillator(); const g = c.createGain();
  o.connect(g); g.connect(c.destination); o.type = type;
  freqs.forEach((f, i) => o.frequency.setValueAtTime(f, c.currentTime + i * (dur / freqs.length)));
  g.gain.setValueAtTime(vol, c.currentTime);
  g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + dur);
  o.start(); o.stop(c.currentTime + dur);
}
const playBulto    = () => tono([1100], 0.07, 0.18);
const playCompleto = () => tono([880, 1318.5], 0.28, 0.2);
const playError    = () => tono([300, 200], 0.22, 0.22, "square");

const LS_KEY = "cargaAgencias:progreso";

export default function EscaneoCargaAgencias({ pedidos, deposito, onClose, onFinalizado }) {
  const { profile } = useAuth();
  const esAdmin = hasRol(profile, "admin");

  // Mapa clave-de-QR → pedidoId, y las claves esperadas por pedido.
  const { keyToPid, keysPorPedido, pedidosMap } = useMemo(() => {
    const keyToPid = {};
    const keysPorPedido = {};
    const pedidosMap = {};
    for (const p of pedidos) {
      pedidosMap[p.id] = p;
      const us = unidadesDeItem({ pedidoId: p.id, bultos: p.bultos, paquetes: p.paquetes });
      keysPorPedido[p.id] = us.map(u => u.key);
      for (const u of us) keyToPid[u.key] = p.id;
    }
    return { keyToPid, keysPorPedido, pedidosMap };
  }, [pedidos]);

  const scannedRef = useRef({}); // { pedidoId: Set<key> }
  const horaInicioRef = useRef(null);
  // `force` dispara un re-render cuando cambia scannedRef (ref mutable). `estados` se
  // calcula inline en cada render (ver abajo), así siempre refleja el ref al instante
  // (sin depender de una lista de dependencias que se pueda desincronizar).
  const [, force] = useState(0);
  const rerender = () => force(n => n + 1);
  const [ultimo, setUltimo]   = useState(null);
  const [error, setError]     = useState("");
  const [saving, setSaving]   = useState(false);
  const [modoManual, setModoManual] = useState(false); // etiqueta rota / sin escáner → marcar a mano
  const [confirmParcial, setConfirmParcial] = useState(null); // [{p,size,total}] pendiente de confirmar

  // Respaldo local: recuperar al montar (si la tablet se recargó a mitad de carga).
  useEffect(() => {
    horaInicioRef.current = new Date().toISOString();
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && saved.deposito === deposito && saved.scanned) {
          const restored = {};
          for (const [pid, keys] of Object.entries(saved.scanned)) {
            const validas = (keysPorPedido[pid] || []);
            // Aceptamos claves reales o el marcador manual de pedidos sin unidades.
            const filtradas = keys.filter(k => validas.includes(k) || String(k).endsWith("|manual"));
            if (filtradas.length) restored[pid] = new Set(filtradas);
          }
          if (Object.keys(restored).length) {
            scannedRef.current = restored;
            if (saved.horaInicio) horaInicioRef.current = saved.horaInicio;
            rerender();
          }
        }
      }
    } catch { /* localStorage no disponible → seguimos sin respaldo */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function persistLocal() {
    try {
      const scanned = {};
      for (const [pid, set] of Object.entries(scannedRef.current)) scanned[pid] = [...set];
      localStorage.setItem(LS_KEY, JSON.stringify({ deposito, horaInicio: horaInicioRef.current, scanned }));
    } catch { /* sin respaldo, no bloquea */ }
  }

  // Chequeo de factura (candado): sin factura no se carga (salvo admin).
  function bloqueadoPorFactura(pedido) {
    return pedido.facturado !== true && !esAdmin;
  }

  const handleScan = useCallback((raw) => {
    const texto = (typeof raw === "string" ? raw : raw?.text || raw?.decodedText || String(raw)).trim();
    const pid = keyToPid[texto];
    if (!pid) {
      setError("QR no reconocido — el pedido no está despachado/facturado, o no es una etiqueta de agencia");
      playError(); rerender(); return;
    }
    const pedido = pedidosMap[pid];
    if (bloqueadoPorFactura(pedido)) {
      setError(`#${pedido.numero || pid} sin facturar — no se puede cargar`);
      playError(); rerender(); return;
    }
    if (!scannedRef.current[pid]) scannedRef.current[pid] = new Set();
    const set = scannedRef.current[pid];
    if (set.has(texto)) return; // ya escaneado → ignorar sin re-beep
    set.add(texto);
    persistLocal();
    setError("");
    const total = (keysPorPedido[pid] || []).length || 1;
    if (set.size >= total) { playCompleto(); setUltimo({ numero: pedido.numero || pid, completo: true, size: set.size, total }); }
    else { playBulto(); setUltimo({ numero: pedido.numero || pid, completo: false, size: set.size, total }); }
    rerender();
  }, [keyToPid, pedidosMap, keysPorPedido, esAdmin]);

  // Marcar/desmarcar TODO un pedido a mano (etiqueta rota o sin escáner).
  function toggleManual(pid) {
    const pedido = pedidosMap[pid];
    if (bloqueadoPorFactura(pedido)) { setError(`#${pedido.numero || pid} sin facturar — no se puede cargar`); playError(); return; }
    const keys = keysPorPedido[pid] || [];
    const total = keys.length || 1; // igual que en `estados` (0 unidades cuenta como 1)
    const set = scannedRef.current[pid] || new Set();
    const yaCompleto = set.size >= total && set.size > 0;
    if (yaCompleto) {
      scannedRef.current[pid] = new Set(); // estaba completo → lo vaciamos
    } else {
      // Marcar completo. Si el pedido NO tiene bultos ni paquetes (0 unidades), usamos
      // un marcador para que igual cuente como cargado — si no, con un Set vacío nunca
      // llegaría a "completo" (size 0 >= total 1 es falso) y no se podría cargar a mano.
      scannedRef.current[pid] = keys.length ? new Set(keys) : new Set([`${pid}|manual`]);
      playCompleto();
    }
    persistLocal();
    setError("");
    rerender();
  }

  // Estado por pedido. Se calcula INLINE en cada render (no useMemo): lee el ref actual,
  // así tanto el escaneo como la marca manual se reflejan al instante. Es barato (son
  // pocos pedidos) y elimina el bug de "se marca recién en el próximo click".
  const estados = pedidos.map(p => {
    const set = scannedRef.current[p.id];
    const size = set ? set.size : 0;
    const total = (keysPorPedido[p.id] || []).length || 1;
    return { p, size, total, completo: size >= total, parcial: size > 0 && size < total };
  });

  const completos = estados.filter(e => e.completo);
  const parciales = estados.filter(e => e.parcial);

  // Orden de la lista:
  //  · En modo ESCANEO: completados al fondo (los pendientes suben, cómodo al escanear).
  //  · En modo MANUAL: orden FIJO (el de `pedidos`). Si reordenaramos al marcar, la fila
  //    tocada saltaria de lugar y otra subiria a su posicion -> se marca el pedido
  //    equivocado en la tablet. Fijo = lo que tocas es lo que se marca.
  const lista = modoManual
    ? estados
    : [...estados].sort((a, b) => (a.completo ? 1 : 0) - (b.completo ? 1 : 0));

  async function finalizar(forzar = false) {
    const parc = estados.filter(e => e.parcial);
    if (parc.length > 0 && !forzar) { setConfirmParcial(parc); return; }
    setConfirmParcial(null);
    const comp = estados.filter(e => e.completo);
    if (comp.length === 0) { setError("No hay pedidos completos para cargar al camión."); return; }
    setSaving(true);
    try {
      // 1) Un solo batch: marcar CARGADO los pedidos completos.
      const batch = writeBatch(db);
      for (const e of comp) {
        batch.update(doc(db, "pedidos_despachados", e.p.id), {
          cargaEstado: "CARGADO",
          cargadoAt: serverTimestamp(),
          // Guardamos las claves escaneadas, sin el marcador interno de 0 unidades.
          bultosEscaneados: [...scannedRef.current[e.p.id]].filter(k => !String(k).endsWith("|manual")),
        });
      }
      await batch.commit();

      // 2) Historial (cargas_agencia): agrupado por agencia, con paquetes y horarios.
      const agenciaOrder = Object.fromEntries(AGENCIAS.map(a => [a.nombre, a.orden]));
      const porAg = {};
      for (const e of comp) {
        const ag = e.p.agencia || "SIN ASIGNAR";
        (porAg[ag] ||= []).push({
          id: e.p.id, numero: e.p.numero || e.p.id, cliente: e.p.cliente || "",
          bultos: Number(e.p.bultos) || 0, paquetes: Number(e.p.paquetes) || 0,
        });
      }
      const agencias = Object.entries(porAg)
        .map(([agencia, peds]) => ({
          agencia, pedidos: peds,
          totalBultos: peds.reduce((s, x) => s + x.bultos, 0),
          totalPaquetes: peds.reduce((s, x) => s + x.paquetes, 0),
        }))
        .sort((a, b) => (agenciaOrder[b.agencia] ?? 99) - (agenciaOrder[a.agencia] ?? 99));

      await addDoc(collection(db, "cargas_agencia"), {
        deposito: deposito || null,
        horaInicio: horaInicioRef.current ? Timestamp.fromDate(new Date(horaInicioRef.current)) : null,
        finalizadoAt: serverTimestamp(),
        finalizadoPorUid: profile?.uid || null,
        finalizadoPorNombre: profile?.nombre || profile?.email || null,
        totalPedidos: comp.length,
        totalBultos: comp.reduce((s, e) => s + (Number(e.p.bultos) || 0), 0),
        totalPaquetes: comp.reduce((s, e) => s + (Number(e.p.paquetes) || 0), 0),
        agencias,
      });

      try { localStorage.removeItem(LS_KEY); } catch { /* noop */ }
      onFinalizado?.({ cargados: comp.length, parciales: parc.length });
    } catch (e) {
      console.error("[CargaAgencias] finalizar", e);
      setError("Error al finalizar: " + (e?.message || e));
      setSaving(false);
    }
  }

  const totalCompletos = completos.length;

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0f172a", zIndex: 1000, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #1e293b" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 800, color: "#fff" }}>🚚 Carga de AGENCIAS <span style={{ fontSize: "10px", fontWeight: 700, color: "#34d399", background: "#064e3b", padding: "1px 6px", borderRadius: "6px", verticalAlign: "middle" }}>v4</span></h3>
          <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#94a3b8" }}>{totalCompletos}/{pedidos.length} pedidos listos</p>
        </div>
        <button onClick={onClose} style={{ background: "#1e293b", border: "none", color: "#fff", borderRadius: "10px", padding: "8px 14px", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}>Cerrar</button>
      </div>

      {/* Cámara + toggle manual */}
      <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", alignItems: "center" }}>
        {!modoManual && <QrScanner onScanSuccess={handleScan} qrbox={{ width: 240, height: 240 }} />}
        <div style={{ marginTop: "8px", minHeight: "24px", textAlign: "center" }}>
          {error ? (
            <span style={{ color: "#fca5a5", fontSize: "13px", fontWeight: 700 }}>⚠️ {error}</span>
          ) : ultimo ? (
            <span style={{ color: ultimo.completo ? "#86efac" : "#bae6fd", fontSize: "13px", fontWeight: 700 }}>
              {ultimo.completo ? `✅ #${ultimo.numero} completo (${ultimo.total})` : `📦 #${ultimo.numero} — ${ultimo.size}/${ultimo.total}`}
            </span>
          ) : (
            <span style={{ color: "#64748b", fontSize: "13px" }}>{modoManual ? "Tocá cada pedido para marcarlo cargado" : "Apuntá a las etiquetas, una tras otra…"}</span>
          )}
        </div>
        <button onClick={() => setModoManual(m => !m)}
          style={{ marginTop: "6px", background: "none", border: "1px solid #334155", color: "#94a3b8", borderRadius: "8px", padding: "5px 12px", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}>
          {modoManual ? "📷 Volver a escanear" : "✋ Marcar a mano (etiqueta rota)"}
        </button>
      </div>

      {/* Lista de pedidos con progreso */}
      <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: "6px" }}>
        {lista.map(({ p, size, total, completo, parcial }) => (
          <div key={p.id} style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: completo ? "#14532d" : parcial ? "#1e3a5f" : "#1e293b",
            border: `1px solid ${completo ? "#16a34a" : parcial ? "#2563eb" : "#334155"}`,
            borderRadius: "10px", padding: "10px 14px", gap: "10px",
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: "13px", fontWeight: 700, color: "#fff" }}>
                {completo ? "✅ " : parcial ? "⏳ " : ""}{p.numero || p.id}
                {p.agencia ? <span style={{ color: "#93c5fd", fontWeight: 600 }}> · {p.agencia}</span> : ""}
              </div>
              <div style={{ fontSize: "12px", color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {p.cliente}
                {(Number(p.bultos) || 0) > 0 ? ` · 📦 ${p.bultos} bultos` : ""}
                {(Number(p.paquetes) || 0) > 0 ? ` · 📏 ${p.paquetes} paq.` : ""}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
              <span style={{ fontSize: "13px", fontWeight: 800, color: completo ? "#86efac" : "#bae6fd" }}>{size}/{total}</span>
              {modoManual && (
                <button onClick={() => toggleManual(p.id)}
                  style={{ background: completo ? "#334155" : "#2563eb", border: "none", color: "#fff", borderRadius: "8px", padding: "5px 10px", fontSize: "12px", fontWeight: 700, cursor: "pointer" }}>
                  {completo ? "Quitar" : "Cargar"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* CTA finalizar */}
      <div style={{ padding: "12px 16px 20px", borderTop: "1px solid #1e293b" }}>
        <button
          onClick={() => finalizar(false)}
          disabled={saving || totalCompletos === 0}
          style={{
            width: "100%", padding: "15px", borderRadius: "12px", border: "none",
            background: saving || totalCompletos === 0 ? "#334155" : "#16a34a",
            color: saving || totalCompletos === 0 ? "#64748b" : "#fff",
            fontWeight: 800, fontSize: "15px", cursor: saving || totalCompletos === 0 ? "not-allowed" : "pointer",
          }}
        >
          {saving ? "Guardando…" : `🚚 Finalizar carga de AGENCIAS (${totalCompletos})`}
        </button>
      </div>

      {/* Aviso de cargas parciales */}
      {confirmParcial && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1100, display: "flex", alignItems: "flex-end" }}>
          <div style={{ background: "#fff", width: "100%", borderRadius: "16px 16px 0 0", padding: "20px 20px 28px", maxHeight: "80vh", overflowY: "auto" }}>
            <h3 style={{ margin: "0 0 6px", fontSize: "16px", fontWeight: 800, color: "#9a3412" }}>⚠️ Hay pedidos a medias</h3>
            <p style={{ margin: "0 0 12px", fontSize: "13px", color: "#64748b" }}>
              Estos pedidos tienen paquetes escaneados pero <strong>no todos</strong>. Si finalizás, <strong>NO</strong> se cargan (quedan en la lista para completarlos después):
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "16px" }}>
              {confirmParcial.map(({ p, size, total }) => (
                <div key={p.id} style={{ display: "flex", justifyContent: "space-between", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: "8px", padding: "8px 12px", fontSize: "13px" }}>
                  <span style={{ fontWeight: 700, color: "#9a3412" }}>{p.numero || p.id}{p.agencia ? ` · ${p.agencia}` : ""}</span>
                  <span style={{ fontWeight: 800, color: "#c2410c" }}>{size}/{total}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={() => setConfirmParcial(null)}
                style={{ flex: 1, padding: "13px", borderRadius: "12px", border: "1.5px solid #e2e8f0", background: "#fff", fontWeight: 700, fontSize: "14px", color: "#475569", cursor: "pointer" }}>
                Seguir cargando
              </button>
              <button onClick={() => finalizar(true)} disabled={saving}
                style={{ flex: 2, padding: "13px", borderRadius: "12px", border: "none", background: "#16a34a", color: "#fff", fontWeight: 700, fontSize: "14px", cursor: "pointer" }}>
                Finalizar igual ({completos.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
